import React, { useState, useEffect, useMemo } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const GestionarCatalogo = () => {
  const { user } = useAuth();
  const [nombrePlaca, setNombrePlaca] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registroInfo, setRegistroInfo] = useState(null);
  const [catalogoData, setCatalogoData] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [registroToDelete, setRegistroToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Función para cargar los registros del catálogo
  const fetchCatalogo = async () => {
    if (!user?.id_generado) {
      console.log('⚠️ No hay id_generado del usuario:', user);
      setLoadingCatalogo(false);
      return;
    }

    try {
      const url = new URL(getApiUrl('SUPERVISION', 'CATALOGO'));
      url.searchParams.set('id_generado', user.id_generado);

      console.log('🔍 Cargando catálogo para id_generado:', user.id_generado);
      console.log('🔗 URL:', url.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      console.log('📥 Respuesta del servidor:', {
        ok: response.ok,
        status: response.status,
        success: data.success,
        totalRegistros: data.data?.length || 0,
        registros: data.data
      });

      if (response.ok && data.success) {
        setCatalogoData(data.data || []);
        console.log('✅ Catálogo cargado exitosamente:', data.data?.length || 0, 'registros');
      } else {
        console.error('❌ Error obteniendo catálogo:', data.error || data);
      }
    } catch (err) {
      console.error('❌ Error cargando catálogo:', err);
    } finally {
      setLoadingCatalogo(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchCatalogo();
  }, [user?.id_generado]);

  // Agrupar registros por tipo
  const registrosAgrupados = useMemo(() => {
    const grupos = {
      VEHICULO: [],
      FUNCIONARIO: [],
      CONDUCTOR: []
    };

    catalogoData.forEach(registro => {
      if (grupos[registro.tipo]) {
        grupos[registro.tipo].push(registro);
      }
    });

    return grupos;
  }, [catalogoData]);

  // Nombres de categorías
  const nombresCategorias = {
    VEHICULO: 'Vehículos',
    FUNCIONARIO: 'Funcionarios',
    CONDUCTOR: 'Conductores'
  };

  // Validar que solo los vigilantes puedan acceder
  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No hay usuario autenticado.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (user?.rol !== 'V') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No tienes permisos para acceder a esta sección. Solo los vigilantes pueden acceder.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!user?.id_generado) {
      setError('Tu usuario no tiene un puesto asignado (id_generado). Solicita a un administrador que configure tu acceso.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(getApiUrl('SUPERVISION', 'CATALOGO'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          id_generado: user.id_generado,
          dato: nombrePlaca.trim(),
          descripcion: descripcion.trim(),
          tipo: tipo
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Guardar información del registro antes de limpiar
        setRegistroInfo({
          dato: nombrePlaca.trim(),
          tipo: tipo,
          descripcion: descripcion.trim()
        });
        
        setSuccess(data.message || 'Registro guardado correctamente');
        setShowSuccessModal(true);
        
        // Limpiar formulario
        setNombrePlaca('');
        setDescripcion('');
        setTipo('');
        
        // Recargar los datos del catálogo
        fetchCatalogo();
      } else {
        setError(data.error || 'Error al guardar el registro');
      }
    } catch (err) {
      console.error('Error guardando en catálogo:', err);
      setError('Error de conexión al guardar el registro');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccess('');
    setRegistroInfo(null);
  };

  const handleDeleteClick = (registro) => {
    setRegistroToDelete(registro);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setRegistroToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!registroToDelete || !user?.id_generado) {
      return;
    }

    setDeleting(true);
    setError('');

    try {
      const url = new URL(getApiUrl('SUPERVISION', 'CATALOGO') + `/${registroToDelete.id}`);
      url.searchParams.set('id_generado', user.id_generado);

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Recargar los datos del catálogo
        await fetchCatalogo();
        // Cerrar el modal y mostrar mensaje de éxito
        setShowDeleteModal(false);
        setRegistroInfo({
          dato: registroToDelete.Dato,
          tipo: registroToDelete.tipo,
          descripcion: registroToDelete.Descripcion
        });
        setSuccess(data.message || 'Registro eliminado correctamente');
        setShowSuccessModal(true);
        setRegistroToDelete(null);
      } else {
        setError(data.error || 'Error al eliminar el registro');
      }
    } catch (err) {
      console.error('Error eliminando del catálogo:', err);
      setError('Error de conexión al eliminar el registro');
    } finally {
      setDeleting(false);
    }
  };

  const renderTablaCatalogo = () => {
    if (loadingCatalogo) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6 sm:p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-xs sm:text-sm text-gray-600">Cargando catálogo...</p>
        </div>
      );
    }

    const totalRegistros = catalogoData.length;
    if (totalRegistros === 0) {
      return (
        <div className="bg-white border border-gray-200 rounded-xl shadow p-6 sm:p-8 text-center">
          <p className="text-xs sm:text-sm text-gray-600">No hay registros en el catálogo aún</p>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden mb-4">
        <div className="px-2 sm:px-4 py-2 sm:py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Catálogo de Registros</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">Total: {totalRegistros} registro(s)</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {/* Sección Vehículos */}
          {registrosAgrupados.VEHICULO.length > 0 && (
            <div className="p-2 sm:p-3 lg:p-4">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg mb-0">
                <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wide">
                  {nombresCategorias.VEHICULO} ({registrosAgrupados.VEHICULO.length})
                </h3>
              </div>
              <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                <table className="w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs font-semibold text-blue-900 uppercase tracking-wider" style={{ width: '30%' }}>
                        Placa
                      </th>
                      <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs font-semibold text-blue-900 uppercase tracking-wider" style={{ width: '55%' }}>
                        Descripción
                      </th>
                      <th className="px-1 sm:px-2 py-1.5 sm:py-2 text-center text-xs font-semibold text-blue-900 uppercase tracking-wider" style={{ width: '15%' }}>
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {registrosAgrupados.VEHICULO.map((registro) => (
                      <tr key={registro.id} className="hover:bg-blue-50/50 transition-colors">
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-900 align-top break-words">
                          {registro.Dato}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 align-top break-words">
                          {registro.Descripcion}
                        </td>
                        <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-center align-top">
                          <button
                            onClick={() => handleDeleteClick(registro)}
                            className="inline-flex items-center justify-center p-1 sm:p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar registro"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sección Funcionarios */}
          {registrosAgrupados.FUNCIONARIO.length > 0 && (
            <div className="p-2 sm:p-3 lg:p-4">
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg mb-0">
                <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wide">
                  {nombresCategorias.FUNCIONARIO} ({registrosAgrupados.FUNCIONARIO.length})
                </h3>
              </div>
              <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                <table className="w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs font-semibold text-green-900 uppercase tracking-wider" style={{ width: '30%' }}>
                        Nombre
                      </th>
                      <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs font-semibold text-green-900 uppercase tracking-wider" style={{ width: '55%' }}>
                        Descripción
                      </th>
                      <th className="px-1 sm:px-2 py-1.5 sm:py-2 text-center text-xs font-semibold text-green-900 uppercase tracking-wider" style={{ width: '15%' }}>
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {registrosAgrupados.FUNCIONARIO.map((registro) => (
                      <tr key={registro.id} className="hover:bg-green-50/50 transition-colors">
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-900 align-top break-words">
                          {registro.Dato}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 align-top break-words">
                          {registro.Descripcion}
                        </td>
                        <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-center align-top">
                          <button
                            onClick={() => handleDeleteClick(registro)}
                            className="inline-flex items-center justify-center p-1 sm:p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar registro"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sección Conductores */}
          {registrosAgrupados.CONDUCTOR.length > 0 && (
            <div className="p-2 sm:p-3 lg:p-4">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-t-lg mb-0">
                <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wide">
                  {nombresCategorias.CONDUCTOR} ({registrosAgrupados.CONDUCTOR.length})
                </h3>
              </div>
              <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                <table className="w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider" style={{ width: '30%' }}>
                        Nombre
                      </th>
                      <th className="px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider" style={{ width: '55%' }}>
                        Descripción
                      </th>
                      <th className="px-1 sm:px-2 py-1.5 sm:py-2 text-center text-xs font-semibold text-amber-900 uppercase tracking-wider" style={{ width: '15%' }}>
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {registrosAgrupados.CONDUCTOR.map((registro) => (
                      <tr key={registro.id} className="hover:bg-amber-50/50 transition-colors">
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-900 align-top break-words">
                          {registro.Dato}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 align-top break-words">
                          {registro.Descripcion}
                        </td>
                        <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-center align-top">
                          <button
                            onClick={() => handleDeleteClick(registro)}
                            className="inline-flex items-center justify-center p-1 sm:p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar registro"
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6 pb-8 sm:pb-12">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Gestionar Catálogo</h1>
          <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">Administra visitantes frecuentes, vehículos y conductores</p>
        </div>

        {error && (
          <div className="mb-3 sm:mb-4 bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3">
            <p className="text-red-800 text-xs sm:text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-3 sm:mb-4 bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3">
            <p className="text-green-800 text-xs sm:text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label htmlFor="nombre-placa" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Nombre/Placa <span className="text-red-500">*</span>
              </label>
              <input
                id="nombre-placa"
                type="text"
                className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ingresa el nombre o placa"
                value={nombrePlaca}
                onChange={(e) => setNombrePlaca(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div>
              <label htmlFor="tipo" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Tipo <span className="text-red-500">*</span>
              </label>
              <select
                id="tipo"
                className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Selecciona un tipo</option>
                <option value="VEHICULO">Vehículo</option>
                <option value="FUNCIONARIO">Funcionario</option>
                <option value="CONDUCTOR">Conductor</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="descripcion" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Descripción <span className="text-red-500">*</span>
            </label>
            <textarea
              id="descripcion"
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Ingresa una descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="flex items-center justify-end">
            <button
              type="submit"
              className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white text-xs sm:text-sm font-semibold rounded-lg shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center space-x-1 sm:space-x-2">
                  <svg className="animate-spin h-3 w-3 sm:h-4 sm:w-4 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Guardando...</span>
                </span>
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </form>

        {/* Tabla de registros del catálogo */}
        <div className="mt-4 sm:mt-6">
          {renderTablaCatalogo()}
        </div>

        {/* Modal de éxito */}
        {showSuccessModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseSuccessModal();
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con icono de éxito */}
              <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Registro exitoso</h3>
                    <p className="text-sm text-gray-600 mt-0.5">Entrada guardada correctamente en el catálogo</p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-5 space-y-4">
                {/* Información del registro */}
                {registroInfo && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span className="text-sm font-bold text-indigo-600">{registroInfo.dato}</span>
                      </div>
                      <div className="flex items-center gap-2 pt-1">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span className="text-xs text-gray-600">Tipo: <span className="font-semibold">{registroInfo.tipo}</span></span>
                      </div>
                      {registroInfo.descripcion && (
                        <div className="flex items-start gap-2 pt-1">
                          <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-xs text-gray-600">{registroInfo.descripcion}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mensaje de confirmación */}
                <div className="text-center">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {success || 'El registro ha sido guardado correctamente en el catálogo.'}
                  </p>
                </div>

                {/* Indicador visual */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500">Registro confirmado</span>
                </div>
              </div>

              {/* Footer con botón */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseSuccessModal}
                  className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmación de eliminación */}
        {showDeleteModal && registroToDelete && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
            style={{ zIndex: 9999 }}
            onClick={(e) => {
              if (e.target === e.currentTarget && !deleting) {
                handleCloseDeleteModal();
              }
            }}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header con icono de advertencia */}
              <div className="px-6 py-5 bg-gradient-to-r from-red-50 to-rose-50 border-b border-red-100">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Confirmar eliminación</h3>
                    <p className="text-sm text-gray-600 mt-0.5">¿Estás seguro de eliminar este registro?</p>
                  </div>
                </div>
              </div>

              {/* Contenido del modal */}
              <div className="px-6 py-5 space-y-4">
                {/* Información del registro a eliminar */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-sm font-bold text-indigo-600">{registroToDelete.Dato}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-xs text-gray-600">Tipo: <span className="font-semibold">{registroToDelete.tipo}</span></span>
                    </div>
                    {registroToDelete.Descripcion && (
                      <div className="flex items-start gap-2 pt-1">
                        <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs text-gray-600">{registroToDelete.Descripcion}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mensaje de advertencia */}
                <div className="text-center">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Esta acción marcará el registro como inactivo. No podrás deshacer esta acción.
                  </p>
                </div>

                {/* Indicador visual */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500">Acción permanente</span>
                </div>
              </div>

              {/* Footer con botones */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseDeleteModal}
                  disabled={deleting}
                  className="flex-1 inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="flex-1 inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
                >
                  {deleting ? (
                    <span className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Eliminando...</span>
                    </span>
                  ) : (
                    'Confirmar eliminación'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GestionarCatalogo;

